'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { bool, number, object, string } from 'yup';

import { Button } from '@/src/shared/presentation/components/button';
import { ControlledCheckbox } from '@/src/shared/presentation/components/checkbox';
import { ControlledInput } from '@/src/shared/presentation/components/input';
import { ControlledPhoneInput } from '@/src/shared/presentation/components/phone-input/phone-input';
import { ControlledRecaptchaInput } from '@/src/shared/presentation/components/recaptcha-input/recaptcha-input';
import { ControlledSelect } from '@/src/shared/presentation/components/select/select';
import { withProviders } from '@/src/shared/presentation/utils/with-providers';

import { CMSBlockTitle } from '../../../../../cms/presentation/components/cms-block-title/cms-block-title';
import SendContactFormProvider, {
  useSendContactFormProvider
} from '../../../providers/send-contact-form-provider';

import css from './contact-block-view.css';

import type ContactFormData from '../../../view-models/contact-form-data';
import type { ObjectSchema } from 'yup';

interface ContactBlockViewProps {
  titleTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

function ContactBlockView({ titleTag }: ContactBlockViewProps) {
  const { isLoading, sendContactForm } = useSendContactFormProvider((state) => state);
  const { t } = useTranslation('contact', { keyPrefix: 'components.contact_block_view' });
  const defaultValues: ContactFormData = {
    name: '',
    surnames: '',
    email: '',
    company: '',
    phone: '',
    rate: '',
    numberOfPeople: 0,
    city: '',
    consent: false,
    recaptcha: false
  };
  const validationSchema: ObjectSchema<ContactFormData> = useMemo(
    () =>
      object().shape({
        name: string().required().min(1),
        surnames: string().required().min(1),
        email: string().required().email(),
        company: string().required().min(1),
        phone: string().required().min(1),
        rate: string().required().min(1),
        numberOfPeople: number().required().min(1),
        city: string().required().min(1),
        consent: bool().required().isTrue(),
        recaptcha: bool().required().isTrue()
      }),
    []
  );
  const form = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema, { abortEarly: false }),
    reValidateMode: 'onChange'
  });
  const rateOptions = useMemo(
    () => [
      { value: 'rate-a', label: 'Rate A' },
      { value: 'rate-b', label: 'Rate B' },
      { value: 'rate-c', label: 'Rate C' }
    ],
    []
  );
  const cityOptions = useMemo(
    () => [
      { value: 'amsterdam', label: 'Amsterdam' },
      { value: 'berlin', label: 'Berlin' },
      { value: 'brussels', label: 'Brussels' },
      { value: 'london', label: 'London' },
      { value: 'paris', label: 'Paris' }
    ],
    []
  );
  const onSubmit = form.handleSubmit(async (data: ContactFormData) => {
    await sendContactForm(data);
  });

  return (
    <div className={css.wrapper}>
      <div className={css.container}>
        <div className={css.leftContent}>
          <CMSBlockTitle titleTag={titleTag} value={t('title')} />
          {t('message_paragraphs', { returnObjects: true }).map((paragraph, index) => (
            <p key={index} className={css.message}>
              {paragraph}
            </p>
          ))}
        </div>
        <div className={css.rightContent}>
          <FormProvider {...form}>
            <form className={css.form} onSubmit={onSubmit}>
              <div className={css.formRow}>
                <ControlledInput
                  name="name"
                  label={t('fields.name.label')}
                  className={css.halfWidthInput}
                />
                <ControlledInput
                  name="surname"
                  label={t('fields.surnames.label')}
                  className={css.halfWidthInput}
                />
              </div>
              <ControlledInput name="email" label={t('fields.email.label')} />

              <div className={css.formRow}>
                <ControlledInput
                  name="company"
                  label={t('fields.company.label')}
                  className={css.halfWidthInput}
                />
                <ControlledPhoneInput
                  name="phone"
                  label={t('fields.phone.label')}
                  className={css.halfWidthInput}
                />
              </div>

              <div className={css.formRow}>
                <ControlledSelect
                  name="rate"
                  options={rateOptions}
                  label={t('fields.rate.label')}
                  className={css.halfWidthInput}
                />
                <ControlledInput
                  name="numberOfPeople"
                  type="number"
                  label={t('fields.number_of_people.label')}
                  className={css.quarterWidthInput}
                />
                <ControlledSelect
                  name="city"
                  options={cityOptions}
                  label={t('fields.city.label')}
                  className={css.quarterWidthInput}
                />
              </div>

              <ControlledCheckbox name="consent" label={t('fields.consent.label')} />

              <ControlledRecaptchaInput name="recaptcha" />

              <div className={css.submitButtonWrapper}>
                <Button type="submit" disabled={!form.formState.isValid || isLoading}>
                  {t('submit_button')}
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
}

export default withProviders(SendContactFormProvider)(ContactBlockView);
